// src/errors/ocr-errors.ts
var OcrError = class extends Error {
  errorCode;
  constructor(errorCode, message, options) {
    super(message, options);
    this.name = "OcrError";
    this.errorCode = errorCode;
  }
};
var TesseractInitError = class extends OcrError {
  constructor(message, options) {
    super("TESSERACT_INIT_FAILED", message, options);
    this.name = "TesseractInitError";
  }
};
var TesseractNotInitializedError = class extends OcrError {
  constructor(message = "OCR provider not initialized \u2014 call initialize() first") {
    super("TESSERACT_NOT_INITIALIZED", message);
    this.name = "TesseractNotInitializedError";
  }
};
var OcrLowConfidenceError = class extends OcrError {
  confidence;
  constructor(confidence, message) {
    super(
      "OCR_LOW_CONFIDENCE",
      message ?? `OCR confidence too low (${confidence}%) \u2014 manual entry required (T3 tier)`
    );
    this.name = "OcrLowConfidenceError";
    this.confidence = confidence;
  }
};
var OcrTimeoutError = class extends OcrError {
  timeoutMs;
  constructor(timeoutMs) {
    super("OCR_TIMEOUT", `OCR extraction exceeded timeout of ${timeoutMs}ms`);
    this.name = "OcrTimeoutError";
    this.timeoutMs = timeoutMs;
  }
};
var OcrMrzParseError = class extends OcrError {
  constructor(reason) {
    super("OCR_MRZ_PARSE_FAILED", `MRZ parse failed: ${reason}`);
    this.name = "OcrMrzParseError";
  }
};
var OcrInvalidInputError = class extends OcrError {
  constructor(reason) {
    super("OCR_INVALID_INPUT", `OCR input invalid: ${reason}`);
    this.name = "OcrInvalidInputError";
  }
};

// src/interfaces/ocr-provider.interface.ts
var OCR_PROVIDER_TOKEN = /* @__PURE__ */ Symbol("OCR_PROVIDER");

// src/services/confidence-gate.ts
var CONFIDENCE_THRESHOLDS = Object.freeze({
  T1_AUTO_PREFILL: 85,
  T2_HIGHLIGHT: 60
});
function gradeConfidence(score) {
  if (Number.isNaN(score)) return "T3";
  if (score < 0) return "T3";
  if (score >= CONFIDENCE_THRESHOLDS.T1_AUTO_PREFILL) return "T1";
  if (score >= CONFIDENCE_THRESHOLDS.T2_HIGHLIGHT) return "T2";
  return "T3";
}

// src/services/mrz-parser.ts
function parseMrz(raw) {
  if (typeof raw !== "string" || raw.trim().length === 0) {
    throw new OcrMrzParseError("empty input");
  }
  const lines = normalizeLines(raw);
  if (lines.length === 2 && lines[0].length === 44 && lines[1].length === 44) {
    return parseTd3(lines[0], lines[1]);
  }
  if (lines.length === 3 && lines[0].length === 30 && lines[1].length === 30 && lines[2].length === 30) {
    return parseTd1(lines[0], lines[1], lines[2]);
  }
  throw new OcrMrzParseError(
    `unrecognized MRZ shape (${lines.length} lines, lengths: ${lines.map((l) => l.length).join(",")})`
  );
}
function parseTd3(line1, line2) {
  const docKind = line1.slice(0, 2);
  const issuer = line1.slice(2, 5);
  const nameField = line1.slice(5, 44);
  const { surname, givenNames } = splitNameField(nameField);
  const docNumberRaw = line2.slice(0, 9);
  const docNumber = stripFillers(docNumberRaw);
  const docNumberChk = line2.slice(9, 10);
  const nationality = stripFillers(line2.slice(10, 13));
  const dobRaw = line2.slice(13, 19);
  const dobChk = line2.slice(19, 20);
  const sex = line2.slice(20, 21);
  const expiryRaw = line2.slice(21, 27);
  const expiryChk = line2.slice(27, 28);
  const docNumberOk = verifyCheckDigit(docNumberRaw, docNumberChk);
  const dobOk = verifyCheckDigit(dobRaw, dobChk);
  const expiryOk = verifyCheckDigit(expiryRaw, expiryChk);
  const fields = {};
  if (docNumber) fields.idCardNumber = docNumber;
  if (nationality) fields.nationality = nationality;
  if (sex === "M" || sex === "F" || sex === "X") fields.sex = sex;
  const dob = yymmddToIso(dobRaw);
  if (dob) fields.dateOfBirth = dob;
  const exp = yymmddToIso(expiryRaw);
  if (exp) fields.idCardExpiry = exp;
  if (surname) fields.surname = surname;
  if (givenNames) fields.givenNames = givenNames;
  fields.idCardType = mapDocKindTd3(docKind, issuer);
  return {
    fields,
    checksumValid: docNumberOk && dobOk && expiryOk,
    format: "TD3"
  };
}
function parseTd1(line1, line2, line3) {
  const docKind = line1.slice(0, 2);
  const issuer = line1.slice(2, 5);
  const docNumberRaw = line1.slice(5, 14);
  const docNumber = stripFillers(docNumberRaw);
  const docNumberChk = line1.slice(14, 15);
  const dobRaw = line2.slice(0, 6);
  const dobChk = line2.slice(6, 7);
  const sex = line2.slice(7, 8);
  const expiryRaw = line2.slice(8, 14);
  const expiryChk = line2.slice(14, 15);
  const nationality = stripFillers(line2.slice(15, 18));
  const { surname, givenNames } = splitNameField(line3);
  const docNumberOk = verifyCheckDigit(docNumberRaw, docNumberChk);
  const dobOk = verifyCheckDigit(dobRaw, dobChk);
  const expiryOk = verifyCheckDigit(expiryRaw, expiryChk);
  const fields = {};
  if (docNumber) fields.idCardNumber = docNumber;
  if (nationality) fields.nationality = nationality;
  if (sex === "M" || sex === "F" || sex === "X") fields.sex = sex;
  const dob = yymmddToIso(dobRaw);
  if (dob) fields.dateOfBirth = dob;
  const exp = yymmddToIso(expiryRaw);
  if (exp) fields.idCardExpiry = exp;
  if (surname) fields.surname = surname;
  if (givenNames) fields.givenNames = givenNames;
  fields.idCardType = mapDocKindTd1(docKind, issuer);
  return {
    fields,
    checksumValid: docNumberOk && dobOk && expiryOk,
    format: "TD1"
  };
}
function computeCheckDigit(input) {
  const weights = [7, 3, 1];
  let sum = 0;
  for (let i = 0; i < input.length; i++) {
    sum += charValue(input[i]) * weights[i % 3];
  }
  return sum % 10;
}
function verifyCheckDigit(input, expected) {
  if (expected.length !== 1) return false;
  if (expected === "<") {
    return true;
  }
  const expectedDigit = Number.parseInt(expected, 10);
  if (!Number.isFinite(expectedDigit)) return false;
  return computeCheckDigit(input) === expectedDigit;
}
function charValue(ch) {
  if (ch === "<") return 0;
  if (ch >= "0" && ch <= "9") return ch.charCodeAt(0) - 48;
  if (ch >= "A" && ch <= "Z") return ch.charCodeAt(0) - 55;
  return 0;
}
function yymmddToIso(yymmdd) {
  if (yymmdd.length !== 6 || !/^\d{6}$/.test(yymmdd)) return void 0;
  const yy = Number.parseInt(yymmdd.slice(0, 2), 10);
  const mm = Number.parseInt(yymmdd.slice(2, 4), 10);
  const dd = Number.parseInt(yymmdd.slice(4, 6), 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return void 0;
  const yyyy = yy >= 50 ? 1900 + yy : 2e3 + yy;
  return `${yyyy.toString().padStart(4, "0")}-${mm.toString().padStart(2, "0")}-${dd.toString().padStart(2, "0")}`;
}
function stripFillers(raw) {
  return raw.replace(/</g, "").trim();
}
function splitNameField(field) {
  const stripped = field.replace(/<+$/, "");
  const parts = stripped.split("<<");
  const surnameRaw = parts[0] ?? "";
  const givenRaw = parts[1] ?? "";
  const surname = surnameRaw.replace(/</g, " ").trim() || void 0;
  const givenNames = givenRaw.replace(/</g, " ").trim() || void 0;
  const result = {};
  if (surname) result.surname = surname;
  if (givenNames) result.givenNames = givenNames;
  return result;
}
function normalizeLines(raw) {
  return raw.replace(/\r/g, "").split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
}
function mapDocKindTd3(docKind, issuer) {
  if (docKind.startsWith("P")) {
    return issuer === "HUN" ? "HU_PASSPORT" : "EU_PASSPORT";
  }
  return "OTHER";
}
function mapDocKindTd1(docKind, issuer) {
  if (docKind.startsWith("I")) {
    return issuer === "HUN" ? "HU_ID_CARD" : "EU_ID_CARD";
  }
  return "OTHER";
}

// src/services/in-memory-ocr.provider.ts
var DEFAULT_RESULT = {
  fields: {
    idCardNumber: "ABC123456",
    idCardExpiry: "2030-05-15",
    idCardType: "HU_PASSPORT",
    surname: "KISS",
    givenNames: "ANNA M\xC1RIA",
    dateOfBirth: "1990-03-12",
    nationality: "HUN",
    sex: "F"
  },
  rawText: "P<HUNKISS<<ANNA<MARIA<<<<<<<<<<<<<<<<<<<<<<<\nABC123456<8HUN9003128F3005151<<<<<<<<<<<<<<04",
  confidence: 92,
  tier: "T1",
  mrzChecksumValid: true,
  durationMs: 1234
};
var InMemoryOcrProvider = class {
  ready;
  canned;
  nextFailure;
  constructor(options = {}) {
    this.ready = options.autoReady ?? false;
    this.canned = options.initialResult ?? { ...DEFAULT_RESULT };
  }
  async initialize() {
    this.ready = true;
  }
  isReady() {
    return this.ready;
  }
  async extract(image) {
    if (!this.ready) {
      throw new TesseractNotInitializedError();
    }
    if (image == null) {
      throw new OcrInvalidInputError("image input is null/undefined");
    }
    const queued = this.nextFailure;
    this.nextFailure = void 0;
    if (queued) {
      if (queued.kind === "lowConfidence") {
        throw new OcrLowConfidenceError(queued.confidence);
      }
      if (queued.kind === "timeout") {
        throw new OcrTimeoutError(queued.timeoutMs);
      }
      throw new OcrInvalidInputError(queued.reason);
    }
    return {
      ...this.canned,
      fields: { ...this.canned.fields }
    };
  }
  parseMrz(mrzRaw) {
    const result = parseMrz(mrzRaw);
    return result.fields;
  }
  async terminate() {
    this.ready = false;
  }
  // ============================================
  // Test-harness setters (NOT part of IOcrProvider)
  // ============================================
  setResult(result) {
    this.canned = {
      ...result,
      tier: result.tier ?? gradeConfidence(result.confidence)
    };
  }
  failNextExtract(failure) {
    this.nextFailure = failure;
  }
};
export {
  CONFIDENCE_THRESHOLDS,
  InMemoryOcrProvider,
  OCR_PROVIDER_TOKEN,
  OcrError,
  OcrInvalidInputError,
  OcrLowConfidenceError,
  OcrMrzParseError,
  OcrTimeoutError,
  TesseractInitError,
  TesseractNotInitializedError,
  gradeConfidence
};
//# sourceMappingURL=index.mjs.map